import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import {
  FunctionJsonSchema,
  FunctionParameterJsonSchema,
  FunctionReturnTypeJsonSchema,
} from 'dashboard/redux/slices/object-schemas';
import { FieldSchemaType } from 'lib/fields';

export enum JsonSchemaTypes {
  Array = 'array',
  Boolean = 'boolean',
  Integer = 'integer',
  Number = 'number',
  Object = 'object',
  String = 'string',
}

export type JsonSchemaBasicTypes =
  | JsonSchemaTypes.Boolean
  | JsonSchemaTypes.Integer
  | JsonSchemaTypes.Number
  | JsonSchemaTypes.String;

export interface JsonSchemaBase {
  title?: string;
  description?: string;
  examples?: string[];
  oneOf?: JsonSchema[];
  $ref?: string;
  $id?: string;
  id?: string; // custom
}

export interface JsonSchemaArray<ItemSchema = JsonSchema> extends JsonSchemaBase {
  type: JsonSchemaTypes.Array;
  items: ItemSchema;
  uniqueItems?: boolean;
  fieldType?: FieldSchemaType.Array | FieldSchemaType.MultiChoices; // custom
}

export interface JsonSchemaBoolean extends JsonSchemaBase {
  type: JsonSchemaTypes.Boolean;
  fieldType?: FieldSchemaType.Boolean; // custom
}

export interface JsonSchemaInteger extends JsonSchemaBase {
  type: JsonSchemaTypes.Integer;
  enum?: number[];
  minimum?: number;
  maximum?: number;
  fieldType?: FieldSchemaType.Integer; // custom
}

export interface JsonSchemaNumber extends JsonSchemaBase {
  type: JsonSchemaTypes.Number;
  enum?: number[];
  minimum?: number;
  maximum?: number;
  fieldType?: FieldSchemaType.Number; // custom
}

export interface JsonSchemaObject<PropertySchema = JsonSchema> extends JsonSchemaBase {
  type: JsonSchemaTypes.Object;
  properties: Record<string, PropertySchema>;
  required?: string[];
  // custom
  objectKey?: WorkflowFieldObjectKey;
  // custom
  fieldType?:
    | FieldSchemaType.File
    | FieldSchemaType.Hash
    | FieldSchemaType.Relation
    | FieldSchemaType.DocumentGeneric
    | FieldSchemaType.GovernmentId
    | FieldSchemaType.GovernmentIdNfc
    | FieldSchemaType.Selfie;
}

export const EMPTY_JSON_SCHEMA_OBJECT: JsonSchemaObject = {
  type: JsonSchemaTypes.Object,
  properties: {},
};

export interface JsonSchemaString extends JsonSchemaBase {
  type: JsonSchemaTypes.String;
  enum?: string[];
  oneOf?: JsonSchemaConst[];
  const?: string;
  format?: string;
  group?: string; // custom
  labels?: string[]; // custom
  maxLength?: number;
  fieldType?:
    | FieldSchemaType.Choices
    | FieldSchemaType.Date
    | FieldSchemaType.Datetime
    | FieldSchemaType.EmailAddress
    | FieldSchemaType.String; // custom
}

export interface JsonSchemaAnyOf extends JsonSchemaBase {
  anyOf: JsonSchema[];
}

export interface JsonSchemaConst extends JsonSchemaBase {
  const: string;
}

export interface JsonSchemaRef extends JsonSchemaBase {
  $ref: string;
}

export type JsonSchemaEmpty = Record<string, never>;

export type JsonSchemaBasic =
  | JsonSchemaBoolean
  | JsonSchemaInteger
  | JsonSchemaNumber
  | JsonSchemaString;

// This is an explicit type for json schemas that only use the standard types.
// e.g. All subschemas will have a `type` attribute.
// This is useful for when you know you won't be dealing with any uncommon schemas like anyOf.
export type JsonSchemaCore =
  | JsonSchemaArray<JsonSchemaCore>
  | JsonSchemaBoolean
  | JsonSchemaInteger
  | JsonSchemaNumber
  | JsonSchemaObject<JsonSchemaCore>
  | JsonSchemaString;

export type JsonSchema =
  | JsonSchemaArray
  | JsonSchemaBoolean
  | JsonSchemaInteger
  | JsonSchemaNumber
  | JsonSchemaObject
  | JsonSchemaString
  | JsonSchemaAnyOf
  | JsonSchemaConst
  | JsonSchemaRef
  | JsonSchemaBase;

export const isJsonSchemaObject = (schema: JsonSchema): schema is JsonSchemaObject => {
  return 'type' in schema && schema.type === JsonSchemaTypes.Object;
};

export const isJsonSchemaArray = (schema: JsonSchema): schema is JsonSchemaArray => {
  return 'type' in schema && schema.type === JsonSchemaTypes.Array;
};

export const isJsonSchemaRef = (schema: JsonSchema): schema is JsonSchemaRef => {
  return '$ref' in schema;
};

export const isJsonSchemaString = (schema: JsonSchema): schema is JsonSchemaString => {
  return 'type' in schema && schema.type === JsonSchemaTypes.String;
};

export const isFunctionSchema = (schema: JsonSchema): schema is FunctionJsonSchema => {
  return schema && 'function' in schema && !!schema.function;
};

export const isGenericArrayItemSchema = (
  object: JsonSchema,
  schema: FunctionParameterJsonSchema | FunctionReturnTypeJsonSchema,
): object is JsonSchemaArray => {
  return (
    'genericType' in schema &&
    schema.genericType === 'array_item_schema' &&
    'type' in object &&
    object.type === JsonSchemaTypes.Array
  );
};

export const functionParametersFromJsonSchema = (
  functionSchema: JsonSchema,
  currentSchema: JsonSchema,
): JsonSchema[] => {
  if (!isFunctionSchema(functionSchema)) {
    return [];
  }

  const paramTypes = functionSchema.properties.parameterTypes;
  const orderedParams: JsonSchema[] = [];
  Object.entries(paramTypes.properties).forEach(([title, paramObject]) => {
    orderedParams[paramObject.position] = {
      title,
      ...(isGenericArrayItemSchema(currentSchema, paramObject) ? currentSchema.items : paramObject),
    };
  });
  return orderedParams;
};

export const returnType = (
  functionSchema: FunctionJsonSchema,
  currentSchema: JsonSchema,
): JsonSchema => {
  const returnType = functionSchema.properties.returnType;

  return isGenericArrayItemSchema(currentSchema, returnType)
    ? currentSchema.items
    : 'genericType' in returnType
      ? EMPTY_JSON_SCHEMA_OBJECT
      : returnType;
};
